import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  isProblemError,
  type CallUpAnsweredRead,
  type MatchRead,
  type MatchSide,
  type Me,
  type TeamSheetRead,
} from '@volee/api-client'

import { meQueryOptions } from '../auth/me.ts'
import { isAnswered, type CallUpOfSheet } from './call-up-answer.ts'
import { clubOrgChartQuery } from './club.ts'
import { bySurname, personFullName } from './labels.ts'
import { sideForViewer, teamSheetQuery } from './lineup-team-sheet.ts'

// The match page's Convocati: the convocazione the reader is allowed to read, and the answer each
// of their own is waiting for. apps/frontend/docs/campionati-e-partite.md § «I convocati sulla pagina partita»

/** What the section is, from the reader's side: the reads still on their way, one that broke, a
 *  reader the API does not let in (which is no error: the section is simply not theirs), or the
 *  convocazione itself with who may write it. */
export type ConvocatiState =
  | { readonly state: 'pending' }
  | { readonly state: 'error'; readonly read: UseQueryResult<TeamSheetRead> }
  | { readonly state: 'hidden' }
  | {
      readonly state: 'ready'
      readonly side: MatchSide
      readonly sheet: TeamSheetRead
      readonly canEdit: boolean
      readonly me: Me
    }

function isForbidden(error: Error | null): boolean {
  return isProblemError(error) && error.status === 403
}

/** The sides to read: an incarico names the reader's own, and a family names none (`Me` holds no
 *  società for them), so both are asked and the API's 403 tells which is theirs. */
function sidesToRead(match: MatchRead, me: Me): readonly MatchSide[] {
  const own = sideForViewer(match, me)
  return own === null ? [match.home, match.away] : [own]
}

/** What the reads say, once the reader is known. A side that answered with a convocazione wins, the
 *  opponent's trouble notwithstanding; failing that, a read still on its way keeps the section
 *  pending, a read that broke (and was no 403) is the error, and only refusals leave it hidden. */
function stateOf(
  match: MatchRead,
  me: Me,
  sides: readonly MatchSide[],
  reads: readonly UseQueryResult<TeamSheetRead>[],
): ConvocatiState {
  for (const [index, read] of reads.entries()) {
    const side = sides[index]
    if (side === undefined || read.data === undefined) continue
    const canEdit = sideForViewer(match, me)?.enrollment_id === side.enrollment_id
    return { state: 'ready', side, sheet: read.data, canEdit, me }
  }
  if (reads.some((read) => read.isPending)) return { state: 'pending' }
  const broken = reads.find((read) => !isForbidden(read.error))
  return broken === undefined ? { state: 'hidden' } : { state: 'error', read: broken }
}

export function useConvocati(match: MatchRead): ConvocatiState {
  const me = useQuery(meQueryOptions)
  const sides = me.data === undefined ? [] : sidesToRead(match, me.data)
  const reads = useQueries({
    queries: sides.map((side) => teamSheetQuery(match.id, side.enrollment_id)),
  })

  if (me.data === undefined) return me.isPending ? { state: 'pending' } : { state: 'hidden' }
  return stateOf(match, me.data, sides, reads)
}

/** Whoever writes the convocazione finds it until it goes to the analysis, empty or a draft; everyone else
 *  reads it once published, and so does the writer after the analysis. */
export function isShownTo(sheet: TeamSheetRead, canEdit: boolean): boolean {
  if (sheet.published_at !== null) return true
  return canEdit && !sheet.numbers_locked
}

/** The players the reader follows: whose tutela they held until the player turned 14 (spec #278). `follows`
 *  came with that spec, and an API deployed before it does not send it: a frontend ahead of it must not fall
 *  over (docs/sviluppo/trappole.md § «Il deploy dei frontend e dell'API possono divergere»). */
export function followedOf(me: Me): readonly number[] {
  const follows: Me['follows'] | undefined = me.follows
  return follows ?? []
}

/** Who a card is about, to the reader: themselves when they play, their own child, or the player they follow
 *  — read as a child, who answers for themselves and for whom the reader answers nothing (spec #280). */
export type Relation = 'child' | 'followed' | 'self' | null

export function relationOf(personId: number, me: Me): Relation {
  if (personId === me.person_id) return 'self'
  if (me.guardian_of.includes(personId)) return 'child'
  return followedOf(me).includes(personId) ? 'followed' : null
}

/** What the called up have answered, as the coach reads it: how many come, who does not and how many have
 *  yet to say. Those who do not come are the cards themselves, in the order of the list. */
export interface Replies {
  readonly coming: number
  readonly notComing: readonly CallUpAnsweredRead[]
  readonly open: number
}

/** The answers of a published convocazione, for a reader who was sent every one of them: the squad's staff.
 *  `undefined` for anyone else — whoever is told only their own would count the rest as «senza risposta» —
 *  and for a draft, which nobody has had a chance to answer. */
export function repliesOf(
  sheet: TeamSheetRead,
  cards: readonly ConvocatoCard[],
): Replies | undefined {
  if (sheet.published_at === null) return undefined
  const rows = cards.map((card) => card.callUp).filter(isAnswered)
  if (rows.length !== cards.length) return undefined
  return {
    coming: rows.filter((row) => row.answer === 'coming').length,
    notComing: rows.filter((row) => row.answer === 'not_coming'),
    open: rows.filter((row) => row.answer === null).length,
  }
}

export interface ConvocatoCard {
  readonly callUp: CallUpOfSheet
  readonly relation: Relation
}

export type OwnAnswer =
  | { readonly kind: 'called'; readonly card: ConvocatoCard }
  | { readonly kind: 'not-called'; readonly personId: number; readonly firstName: string }

/** The answer for each of the reader's own — themselves first, then their children and the players they
 *  follow — and none for a draft: «è convocato» is said of a convocazione the families can read. */
export function ownAnswers(sheet: TeamSheetRead, me: Me): readonly OwnAnswer[] {
  if (sheet.published_at === null) return []
  const ids = [
    ...(me.person_id === null ? [] : [me.person_id]),
    ...me.guardian_of,
    ...followedOf(me),
  ]
  return ids.flatMap((personId): OwnAnswer[] => {
    const called = sheet.call_ups.find((callUp) => callUp.person_id === personId)
    if (called !== undefined) {
      return [{ kind: 'called', card: { callUp: called, relation: relationOf(personId, me) } }]
    }
    const missing = sheet.not_called_up.find((child) => child.person_id === personId)
    if (missing === undefined) return []
    return [{ kind: 'not-called', personId, firstName: missing.first_name }]
  })
}

/** Everyone called up, as one list: the convocazione chooses no role and no starter, so there is
 *  nothing to split it by. */
export function convocatiOf(sheet: TeamSheetRead, me: Me): readonly ConvocatoCard[] {
  return sheet.call_ups
    .map((callUp) => ({ callUp, relation: relationOf(callUp.person_id, me) }))
    .sort((a, b) => bySurname(a.callUp, b.callUp))
}

/** The coach to name to a family whose child is out: the one the sheet's staff lists (staff is the
 *  distinta's, and no step of the app writes it, so it usually lists none), or the squad's coach in the
 *  società's organigramma. That one is read only when `wanted` and the staff names no coach. */
export function useCoachName(
  sheet: TeamSheetRead,
  side: MatchSide,
  wanted: boolean,
): string | null {
  const listed = sheet.staff.find((member) => member.role === 'coach')
  const chart = useQuery({
    ...clubOrgChartQuery(side.club_id),
    enabled: wanted && listed === undefined,
  })
  if (listed !== undefined) return personFullName(listed)
  const coach = chart.data?.items.find(
    (entry) => entry.role === 'coach' && entry.team_id === side.team_id,
  )
  return coach === undefined ? null : personFullName(coach)
}
